import type { CompanySlug } from "./types.js";

/** Per-company registered hub image origins.
 *
 * Must stay in lockstep with ingest-worker/src/receipt_hub_config.ts
 * `imageUrlOrigins` (dependency recorded at both sites — brief). Render gate
 * is **company-scoped**: a company absent from this map must not validate,
 * including when another company lists the same origin.
 */
export const RECEIPT_IMAGE_ORIGINS_BY_COMPANY: Readonly<
  Record<string, readonly string[]>
> = Object.freeze({
  mjnops: Object.freeze(["https://receipts.mjn-ops.example.test"]),
  // Same synthetic origin as mjnops. The hub-feed configs cannot leave
  // imageUrlOrigins empty, and they do not introduce a new host. Render
  // stays company-scoped: a slug absent from this map still refuses.
  gv33: Object.freeze(["https://receipts.mjn-ops.example.test"]),
  gvkw: Object.freeze(["https://receipts.mjn-ops.example.test"]),
  beechgrove: Object.freeze(["https://receipts.mjn-ops.example.test"]),
  square127: Object.freeze(["https://receipts.mjn-ops.example.test"]),
  crestwood: Object.freeze(["https://receipts.mjn-ops.example.test"]),
  "sor-properties": Object.freeze(["https://receipts.mjn-ops.example.test"]),
  "test-rental": Object.freeze(["https://receipts.mjn-ops.example.test"]),
});

/** Flat union — only for the lockstep drift check (not for render). */
export const RECEIPT_IMAGE_ORIGINS: readonly string[] = Object.freeze(
  Object.values(RECEIPT_IMAGE_ORIGINS_BY_COMPANY).flat(),
);

/**
 * Re-check image_url origin at render, scoped to the queue item's company.
 * Returns the href only when scheme is https and origin is on that company's
 * allowlist; otherwise null (never render a foreign href).
 */
export function safeReceiptImageHref(
  imageUrl: string,
  // The company is a slug, and here it is a LOOKUP KEY into the allowlist
  // below, never words a person reads (the brand guard could not tell the two
  // apart while this was `string`).
  company: CompanySlug,
): string | null {
  const allowed = RECEIPT_IMAGE_ORIGINS_BY_COMPANY[company];
  if (allowed === undefined || allowed.length === 0) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(imageUrl);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  if (!allowed.includes(parsed.origin)) return null;
  return parsed.href;
}
